import { hostServesPagePreview } from '../shared/host-capabilities';
import { isPagePreviewSource } from '../shared/page-preview-url';
export type SourcePreview = { source: string; width: number; height: number };
export class SourcePreviewQueueFullError extends Error {
  constructor() {
    super('Source preview queue is full');
  }
}
type Entry = {
  key: string;
  path: string;
  references: number;
  state: 'queued' | 'loading' | 'ready';
  promise: Promise<SourcePreview>;
  resolve: (value: SourcePreview) => void;
  reject: (reason: Error) => void;
  bytes: number;
};

/** Per renderer: two reads at once, at most eight waiting pages, four retained
 * images/24 MiB. Multiple fields on one exact page share a read and decode. */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function createSourcePreviewCache(load: (path: string) => Promise<SourcePreview>) {
  const entries = new Map<string, Entry>();
  const waitingForSpace = new Set<() => void>();
  let active = 0;
  let spaceNotificationQueued = false;
  const queuedCount = () =>
    [...entries.values()].filter((entry) => entry.state === 'queued').length;
  const notifySpace = () => {
    if (spaceNotificationQueued || queuedCount() >= 8) return;
    spaceNotificationQueued = true;
    queueMicrotask(() => {
      spaceNotificationQueued = false;
      if (queuedCount() >= 8) return;
      const callbacks = [...waitingForSpace];
      waitingForSpace.clear();
      for (const callback of callbacks) callback();
    });
  };
  const prune = () => {
    const ready = [...entries.values()].filter((entry) => entry.state === 'ready');
    let bytes = ready.reduce((total, entry) => total + entry.bytes, 0);
    while (ready.length > 4 || bytes > 24 * 1024 * 1024) {
      const entry = ready.shift()!;
      bytes -= entry.bytes;
      entries.delete(entry.key);
    }
  };
  const pump = () => {
    for (const entry of entries.values()) {
      if (active >= 2) return;
      if (entry.state !== 'queued' || entry.references === 0) continue;
      entry.state = 'loading';
      active++;
      notifySpace();
      void Promise.resolve()
        .then(() => load(entry.path))
        .then(
          (value) => {
            entry.state = 'ready';
            entry.bytes = value.source.length * 2;
            entry.resolve(value);
            prune();
          },
          () => {
            entries.delete(entry.key);
            entry.reject(new Error('Source preview unavailable'));
          },
        )
        .finally(() => {
          active--;
          pump();
        });
    }
  };
  return {
    acquire(key: string, path: string) {
      let entry = entries.get(key);
      if (!entry) {
        if (queuedCount() >= 8)
          return {
            promise: Promise.reject<SourcePreview>(new SourcePreviewQueueFullError()),
            release() {},
          };
        let resolve!: Entry['resolve'], reject!: Entry['reject'];
        const promise = new Promise<SourcePreview>((yes, no) => {
          resolve = yes;
          reject = no;
        });
        entry = { key, path, references: 0, state: 'queued', promise, resolve, reject, bytes: 0 };
        entries.set(key, entry);
      } else {
        entries.delete(key);
        entries.set(key, entry);
      }
      entry.references++;
      pump();
      const owned = entry;
      let released = false;
      return {
        promise: entry.promise,
        release() {
          if (released) return;
          released = true;
          owned.references--;
          if (owned.references === 0 && owned.state === 'queued') {
            entries.delete(owned.key);
            owned.reject(new Error('Source preview is no longer visible'));
            notifySpace();
          }
        },
      };
    },
    onQueueSpace(callback: () => void) {
      waitingForSpace.add(callback);
      notifySpace();
      return () => {
        waitingForSpace.delete(callback);
      };
    },
    stats: () => ({
      active,
      queued: [...entries.values()].filter((entry) => entry.state === 'queued').length,
      retained: [...entries.values()].filter((entry) => entry.state === 'ready').length,
      bytes: [...entries.values()].reduce((total, entry) => total + entry.bytes, 0),
      waitingForSpace: waitingForSpace.size,
    }),
  };
}

/** A queue-full admission is not an image failure. Wait for actual free capacity
 * once, not a polling timer; hide/unmount cancels both waiting and queued work.
 * Read/decode failures are reported once and never retried here. */
export function watchSourcePreview(
  cache: ReturnType<typeof createSourcePreviewCache>,
  key: string,
  path: string,
  ready: (preview: SourcePreview) => void,
  failed: () => void,
) {
  let current = true;
  let release: (() => void) | undefined;
  let stopWaiting: (() => void) | undefined;
  const read = () => {
    if (!current) return;
    const request = cache.acquire(key, path);
    release = request.release;
    void request.promise.then(
      (value) => {
        if (current) ready(value);
      },
      (error) => {
        if (!current) return;
        if (error instanceof SourcePreviewQueueFullError)
          stopWaiting = cache.onQueueSpace(() => {
            stopWaiting = undefined;
            read();
          });
        else failed();
      },
    );
  };
  read();
  return () => {
    current = false;
    stopWaiting?.();
    release?.();
  };
}

/** Field and table-row companions share this renderer-wide admission owner. */
export const sourcePreviewCache = createSourcePreviewCache(async (path) => {
  const desktop = window.capturePointDesktop;
  if (!desktop) throw new Error('Preview unavailable');
  const source = await desktop.readPagePreview(path);
  // Only passive original raster bytes from the existing validated preload.
  // PAIGE (docs/engineering/workbench-port.md, "Focus"): or a page address the host serves itself.
  if (
    !(isPagePreviewSource(source) || hostServesPagePreview(source)) ||
    source.length > 24 * 1024 * 1024
  )
    throw new Error('Preview unavailable');
  const image = new Image();
  image.src = source;
  await image.decode();
  const width = image.naturalWidth,
    height = image.naturalHeight;
  if (!width || !height || width * height > 60_000_000) throw new Error('Preview unavailable');
  return { source, width, height };
});
